import React, { useCallback, useEffect, useState } from "react";
import { shopsAPI, usersAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import Modal from "../components/Modal";
import PermissionEditor from "../components/PermissionEditor";
import { matchingPreset } from "../utils/permissions";

/**
 * Who works in this shop, and what they may do.
 *
 * The owner manages this — not the platform. Each member's permissions are
 * ticked per screen (see PermissionEditor); the API enforces every one of
 * them, whatever buttons this page draws.
 */
const BLANK = {
  email: "",
  name: "",
  phone: "",
  password: "",
  pin: "",
  role: "staff",
  // Filled from the server's cashier preset once the catalogue has loaded
  permissions: [],
};

const BLANK_ACCOUNT = { name: "", phone: "" };
const BLANK_PASSWORD = { current: "", next: "", confirm: "" };

const Staff = () => {
  const { t } = useI18n();
  const { user, updateUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [pinFor, setPinFor] = useState(null);
  const [newPin, setNewPin] = useState("");
  const [editingSelf, setEditingSelf] = useState(false);
  const [accountForm, setAccountForm] = useState(BLANK_ACCOUNT);
  const [passwordForm, setPasswordForm] = useState(BLANK_PASSWORD);
  const [accountBusy, setAccountBusy] = useState(false);
  const [passwordBusy, setPasswordBusy] = useState(false);
  // What permissions exist — the editor draws from it
  const [catalog, setCatalog] = useState(null);
  // The member whose permissions are open for editing: { member, draft }
  const [editingAccess, setEditingAccess] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await shopsAPI.staff();
      setMembers(data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || t("staff.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    shopsAPI
      .permissionCatalog()
      .then(({ data }) => {
        setCatalog(data);
        setForm((current) => ({
          ...current,
          permissions: data.presets.cashier,
        }));
      })
      .catch(() => setError(t("staff.permissions.loadFailed")));
  }, [t]);

  const blankForm = () => ({
    ...BLANK,
    permissions: catalog?.presets.cashier || [],
  });

  // "Cashier", "Manager"… when it is exactly a preset; how many otherwise
  const accessSummary = (member) => {
    if (member.role === "owner") return t("staff.permissions.everything");
    const preset = catalog && matchingPreset(catalog, member.permissions || []);
    return preset
      ? t(`staff.permissions.presets.${preset}`)
      : t("staff.permissions.count", {
          count: (member.permissions || []).length,
        });
  };

  const saveAccess = async () => {
    const { member, draft } = editingAccess;
    setBusy(true);
    setError("");

    try {
      await shopsAPI.updateStaff(member.id, {
        role: draft.role,
        permissions: draft.permissions,
      });
      setEditingAccess(null);
      setNotice(t("staff.permissions.saved", { name: member.name }));
      await load();
    } catch (err) {
      report(err, t("staff.changeFailed"));
    } finally {
      setBusy(false);
    }
  };

  const report = (err, fallback) =>
    setError(
      err.response?.data?.code === "PHONE_TAKEN"
        ? t("staff.phoneTaken")
        : err.response?.data?.code === "PHONE_REQUIRED"
        ? t("staff.phoneRequired")
        : err.response?.data?.message || err.response?.data?.error || fallback,
    );

  const add = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");

    try {
      const { data } = await shopsAPI.addStaff(form);
      setForm(blankForm());
      setAdding(false);
      // An existing customer's (or another shop's) account keeps its own
      // password — say so, or the owner hands over one that doesn't work
      setNotice(
        data.kept_existing_password
          ? t("staff.addedKeptPassword")
          : t("staff.added"),
      );
      await load();
    } catch (err) {
      report(err, t("staff.addFailed"));
    } finally {
      setBusy(false);
    }
  };

  const update = async (member, changes) => {
    setError("");

    try {
      await shopsAPI.updateStaff(member.id, changes);
      await load();
    } catch (err) {
      report(err, t("staff.changeFailed"));
    }
  };

  const savePin = async (member) => {
    setBusy(true);
    setError("");

    try {
      await shopsAPI.updateStaff(member.id, { pin: newPin });
      setPinFor(null);
      setNewPin("");
      setNotice(t("staff.pinSet", { name: member.name }));
      await load();
    } catch (err) {
      report(err, t("staff.pinFailed"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (member) => {
    setError("");

    try {
      await shopsAPI.removeStaff(member.id);
      setNotice(t("staff.removed", { name: member.name }));
      await load();
    } catch (err) {
      report(err, t("staff.removeFailed"));
    }
  };

  const openSelfEdit = (member) => {
    setEditingSelf((open) => !open);
    setAccountForm({ name: member.name || "", phone: member.phone || "" });
    setPasswordForm(BLANK_PASSWORD);
  };

  const saveAccount = async (event) => {
    event.preventDefault();
    setAccountBusy(true);
    setError("");

    try {
      await usersAPI.updateProfile(accountForm);
      updateUser(accountForm);
      setNotice(t("staff.accountUpdated"));
      await load();
    } catch (err) {
      report(err, t("staff.accountUpdateFailed"));
    } finally {
      setAccountBusy(false);
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();
    setError("");

    if (passwordForm.next !== passwordForm.confirm) {
      setError(t("staff.passwordMismatch"));
      return;
    }

    setPasswordBusy(true);

    try {
      await usersAPI.changePassword(passwordForm.current, passwordForm.next);
      setPasswordForm(BLANK_PASSWORD);
      setNotice(t("staff.passwordUpdated"));
    } catch (err) {
      report(err, t("staff.passwordChangeFailed"));
    } finally {
      setPasswordBusy(false);
    }
  };

  const resetPassword = async (member) => {
    if (
      !window.confirm(t("staff.resetPasswordConfirm", { name: member.name }))
    ) {
      return;
    }

    setError("");

    try {
      const { data } = await shopsAPI.resetStaffPassword(member.id);
      setNotice(
        t("staff.passwordResetTo", {
          name: member.name,
          password: data.password,
        }),
      );
    } catch (err) {
      report(err, t("staff.resetPasswordFailed"));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600" />
      </div>
    );
  }

  const field =
    "mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500";

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
            {t("staff.title")}
          </h1>
          <p className="text-gray-600">{t("staff.subtitle")}</p>
        </div>
        <button
          onClick={() => setAdding((open) => !open)}
          className="px-5 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold"
        >
          {adding ? t("common.cancel") : t("staff.add")}
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {notice && !error && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      {adding && (
        <form
          onSubmit={add}
          className="bg-white rounded-xl shadow-lg p-6 space-y-4"
        >
          <p className="text-sm text-gray-600">{t("staff.existingHint")}</p>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("staff.email")}
              </span>
              <input
                type="email"
                required
                dir="ltr"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={field}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("staff.name")}
              </span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={field}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("staff.phone")}{" "}
                <span className="text-gray-400">{t("staff.newOnly")}</span>
              </span>
              <input
                type="tel"
                dir="ltr"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={field}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("staff.password")}{" "}
                <span className="text-gray-400">{t("staff.newOnly")}</span>
              </span>
              <input
                type="password"
                autoComplete="new-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className={field}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("staff.pin")}{" "}
                <span className="text-gray-400">{t("staff.fourDigits")}</span>
              </span>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{4}"
                maxLength={4}
                dir="ltr"
                value={form.pin}
                onChange={(e) =>
                  setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })
                }
                className={field}
              />
            </label>
          </div>

          {catalog && (
            <PermissionEditor
              catalog={catalog}
              value={{ role: form.role, permissions: form.permissions }}
              onChange={(access) => setForm({ ...form, ...access })}
            />
          )}

          <button
            type="submit"
            disabled={busy}
            className="px-6 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-60"
          >
            {busy ? t("staff.adding") : t("staff.addToShop")}
          </button>
        </form>
      )}

      <div className="bg-white rounded-xl shadow-lg p-6">
        <ul className="divide-y divide-gray-100">
          {members.map((member) => {
            const isSelf = member.user_id === user?.id;

            return (
              <li key={member.id} className="py-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 flex items-center gap-2">
                      {member.name}
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          member.role === "owner"
                            ? "bg-primary-100 text-primary-800"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {t(`staff.roles.${member.role}`)}
                      </span>
                      {isSelf && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                          {t("staff.you")}
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-gray-500" dir="ltr">
                      {member.email}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        member.has_pin
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-amber-50 text-amber-700"
                      }`}
                    >
                      {member.has_pin ? t("staff.hasPin") : t("staff.noPin")}
                    </span>

                    <button
                      onClick={() => {
                        setPinFor(pinFor === member.id ? null : member.id);
                        setNewPin("");
                      }}
                      className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
                    >
                      {member.has_pin
                        ? t("staff.changePin")
                        : t("staff.setPin")}
                    </button>

                    <span className="text-xs px-2 py-1 rounded-full bg-primary-50 text-primary-800">
                      {accessSummary(member)}
                    </span>

                    {/* Not on your own row: demoting yourself is how an owner
                      locks themselves out */}
                    {!isSelf && catalog && (
                      <button
                        onClick={() =>
                          setEditingAccess({
                            member,
                            draft: {
                              role: member.role,
                              permissions: member.permissions || [],
                            },
                          })
                        }
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
                      >
                        {t("staff.permissions.edit")}
                      </button>
                    )}

                    {isSelf ? (
                      <button
                        onClick={() => openSelfEdit(member)}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
                      >
                        {t("staff.editAccount")}
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => resetPassword(member)}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
                        >
                          {t("staff.resetPassword")}
                        </button>
                        <button
                          onClick={() => remove(member)}
                          className="px-3 py-1.5 rounded-lg border border-red-200 text-red-700 text-sm hover:bg-red-50"
                        >
                          {t("common.remove")}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {isSelf && editingSelf && (
                  <div className="mt-3 grid gap-4 sm:grid-cols-2 rounded-lg border border-gray-200 p-4">
                    <form onSubmit={saveAccount} className="space-y-3">
                      <p className="text-sm font-semibold text-gray-700">
                        {t("staff.myAccount")}
                      </p>
                      <label className="block">
                        <span className="text-sm font-medium text-gray-700">
                          {t("staff.name")}
                        </span>
                        <input
                          type="text"
                          value={accountForm.name}
                          onChange={(e) =>
                            setAccountForm({
                              ...accountForm,
                              name: e.target.value,
                            })
                          }
                          className={field}
                        />
                      </label>
                      <label className="block">
                        <span className="text-sm font-medium text-gray-700">
                          {t("staff.phone")}
                        </span>
                        <input
                          type="tel"
                          dir="ltr"
                          value={accountForm.phone}
                          onChange={(e) =>
                            setAccountForm({
                              ...accountForm,
                              phone: e.target.value,
                            })
                          }
                          className={field}
                        />
                      </label>
                      <button
                        type="submit"
                        disabled={accountBusy}
                        className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold disabled:opacity-50"
                      >
                        {t("staff.saveAccount")}
                      </button>
                    </form>

                    <form onSubmit={savePassword} className="space-y-3">
                      <p className="text-sm font-semibold text-gray-700">
                        {t("staff.password")}
                      </p>
                      <label className="block">
                        <span className="text-sm font-medium text-gray-700">
                          {t("staff.currentPassword")}
                        </span>
                        <input
                          type="password"
                          autoComplete="current-password"
                          value={passwordForm.current}
                          onChange={(e) =>
                            setPasswordForm({
                              ...passwordForm,
                              current: e.target.value,
                            })
                          }
                          className={field}
                        />
                      </label>
                      <label className="block">
                        <span className="text-sm font-medium text-gray-700">
                          {t("staff.newPassword")}
                        </span>
                        <input
                          type="password"
                          autoComplete="new-password"
                          value={passwordForm.next}
                          onChange={(e) =>
                            setPasswordForm({
                              ...passwordForm,
                              next: e.target.value,
                            })
                          }
                          className={field}
                        />
                      </label>
                      <label className="block">
                        <span className="text-sm font-medium text-gray-700">
                          {t("staff.confirmPassword")}
                        </span>
                        <input
                          type="password"
                          autoComplete="new-password"
                          value={passwordForm.confirm}
                          onChange={(e) =>
                            setPasswordForm({
                              ...passwordForm,
                              confirm: e.target.value,
                            })
                          }
                          className={field}
                        />
                      </label>
                      <button
                        type="submit"
                        disabled={passwordBusy}
                        className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold disabled:opacity-50"
                      >
                        {t("staff.savePassword")}
                      </button>
                    </form>
                  </div>
                )}

                {pinFor === member.id && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={4}
                      autoFocus
                      dir="ltr"
                      placeholder={t("staff.pinPlaceholder")}
                      value={newPin}
                      onChange={(e) =>
                        setNewPin(e.target.value.replace(/\D/g, ""))
                      }
                      className="w-32 px-4 py-2 border border-gray-300 rounded-lg tracking-[0.4em] text-center"
                    />
                    <button
                      onClick={() => savePin(member)}
                      disabled={newPin.length !== 4 || busy}
                      className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold disabled:opacity-50"
                    >
                      {t("staff.savePin")}
                    </button>
                    {member.has_pin && (
                      <button
                        onClick={() =>
                          update(member, { pin: null }).then(() =>
                            setPinFor(null),
                          )
                        }
                        className="px-4 py-2 rounded-lg border border-gray-300 text-sm"
                      >
                        {t("staff.removePin")}
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {editingAccess && (
        <Modal
          title={t("staff.permissions.title", {
            name: editingAccess.member.name,
          })}
          onClose={() => setEditingAccess(null)}
          maxWidthClass="max-w-2xl"
        >
          <PermissionEditor
            catalog={catalog}
            value={editingAccess.draft}
            onChange={(draft) => setEditingAccess({ ...editingAccess, draft })}
          />
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={() => setEditingAccess(null)}
              className="px-5 py-2.5 rounded-lg border border-gray-300 font-semibold hover:bg-gray-50"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={saveAccess}
              disabled={busy}
              className="px-5 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-60"
            >
              {busy ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Staff;
